import React, {
  FormEvent,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import Button from '../../Components/Button';
import ButtonGroup from '../../Components/ButtonGroup';
import { useNavigate } from 'react-router-dom';
import FormRow from '../../Components/FormRow';
import Label from '../../Components/Label';
import Title from '../../Components/Title';
import DataContext from '../../Context';
import { IPolicy } from '../../Models/PolicyInfo';
import './CreateLogin.css';
import { API_ACTION, SaifFetch } from '../../Utils/SaifFetch';
import UsernameInput from '../../Components/UsernameInput';
import useSaifFetch from '../../hooks/useSaifFetch';
import { LoadingSpinner } from '@saif/loading-spinner';
import Password from '../../Components/Password';
import SecurityQuestions from '../../Components/SecurityQuestions';
import useWarnRefresh from '../../hooks/useWarnRefresh';
import { ILocalizationContext } from '../../Localization/Models/ILocalizationContext';
import LocalizationContext from '../../Localization/LanguageContext';

const CreateLogin = () => {
  const {
    username,
    setUsername,
    usernameValid,
    setUsernameValid,
    password,
    setProgressState,
    setErrorPageMsg,
    firstName,
    lastName,
    email,
  } = useContext(DataContext) as IPolicy;
  const { getValue } = useContext(LocalizationContext) as ILocalizationContext;

  const usernameRef = useRef<null | HTMLInputElement>(null);
  const [securityQuestionsValid, setSecurityQuestionsValid] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [required, setRequired] = useState(false);

  // object sent in API call to validate credentials.
  const [body, setBody] = useState({});
  const { data, loading, status, error } = useSaifFetch<any>(
    API_ACTION.Validate_Username,
    body,
    SaifFetch.post
  );

  useWarnRefresh();

  useEffect(() => {
    setProgressState(3);

    if (username) {
      setUsernameValid((prev) => {
        return {
          invalid: false,
          isShort: username.length < 8,
          touched: true,
          empty: false,
          nonLegitChars: prev.nonLegitChars,
        };
      });
    }
  }, []);

  const handleErrorCheck = () => {
    const input = document.querySelector(
      `.input-error,.check-error,.question-required`
    );

    input?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'start',
    });
  };

  useEffect(() => {
    if (status === 200) {
      setUsernameValid((prev) => {
        return {
          invalid: false,
          isShort: prev.isShort,
          touched: true,
          empty: prev.empty,
          nonLegitChars: prev.nonLegitChars,
        };
      });
      navigator('/Review', { replace: true });
    } else if (status === 400) {
      if (data.username[0].toString().includes('already')) {
        usernameRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'start',
        });
        setUsernameValid((prev) => {
          return {
            invalid: true,
            isShort: prev.isShort,
            touched: true,
            empty: false,
            nonLegitChars: prev.nonLegitChars,
          };
        });
      }
      if (data.username[0].toString().includes('invalid')) {
        usernameRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'start',
        });
        setUsernameValid((prev) => {
          return {
            invalid: true,
            isShort: prev.isShort,
            touched: true,
            empty: false,
            nonLegitChars: true,
          };
        });
      }
    } else if ((status && status >= 401) || error) {
      setErrorPageMsg('');
      navigator('/error', { replace: true });
    }
  }, [status, error, data]);

  let navigator = useNavigate();
  const GoBack = () => {
    navigator('/CompanyInfo', { replace: true });
    setUsernameValid(() => {
      return {
        invalid: false,
        touched: false,
        isShort: false,
        empty: false,
        nonLegitChars: false,
      };
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!IsDataValid()) {
      setTimeout(handleErrorCheck, 10);
    }
    if (IsDataValid()) setBody({ username: username });
    else {
      setRequired(true);
    }
  };

  const IsDataValid = () => {
    return (
      isPasswordValid &&
      securityQuestionsValid &&
      username.length > 7 &&
      usernameMatchValid() &&
      UserNameisValid()
    );
  };

  const UserNameisValid = () => {
    return (
      usernameValid.nonLegitChars === false &&
      usernameValid.isShort === false &&
      usernameValid.touched === true
    );
  };

  const usernameMatchValid = () => {
    return !(
      (username as string).toLowerCase() === password.toLowerCase() ||
      password.toLowerCase().includes((username as string).toLowerCase()) ||
      (password.toLowerCase().includes((firstName as string).toLowerCase()) &&
        (firstName as string).length >= 2) ||
      (password.toLowerCase().includes((lastName as string).toLowerCase()) &&
        (lastName as string).length >= 2) ||
      password
        .toLowerCase()
        .includes(
          (email as string)
            .toLowerCase()
            .substring(0, (email as string).indexOf('@'))
        )
    );
  };

  return (
    <div className='CreateLoginPage'>
      {loading && (
        <div className='loading position-fixed'>
          <div className='d-flex justify-content-center loading'>
            <LoadingSpinner text={getValue('CreateLogin', 'Validating')} />
          </div>
        </div>
      )}

      <div className='container form-container px-4'>
        <form onSubmit={handleSubmit} className='create-password-form'>
          <FormRow>
            <Title
              contentDescription={getValue('CreateLogin', 'Title')}
              instructionDescription={getValue('CreateLogin', 'TitleSub')}
            />
          </FormRow>

          <FormRow>
            <Label>{getValue('CreateLogin', 'UsernameLabel')}</Label>
            <small className='username-label text-xs pb-1'>
              {getValue('CreateLogin', 'UsernameLabelSub')}
            </small>
            <UsernameInput
              ref={usernameRef}
              inputId='Username'
              inputValue={username}
              type='text'
              isValid={usernameValid}
              errorMessage={getValue('CreateLogin', 'ValUserRequired')}
              onChange={setUsername}
              setUsernameValid={setUsernameValid}
              maxLength={30}
            />
          </FormRow>

          <Password
            setPasswordIsValid={setIsPasswordValid}
            required={required}
            customValidation={username.length > 7 && usernameMatchValid()}
          />

          <FormRow>
            <Title
              contentDescription={getValue('CreateLogin', 'QuestionsLabel')}
              instructionDescription=''
            />
          </FormRow>
          <SecurityQuestions
            setSecurityQuestionsAreValid={setSecurityQuestionsValid}
            required={required}
          />

          <ButtonGroup>
            <span className='col-md-6 my-2'>
              <Button
                type='button'
                version='secondary'
                bootstrap='col-12'
                onClick={GoBack}
              >
                {getValue('CreateLogin', 'Back')}
              </Button>
            </span>
            <span className='col-md-6 my-2'>
              <Button
                type='submit'
                bootstrap='col-12'
                version={IsDataValid() ? 'primary' : 'disabled'}
              >
                {getValue('CreateLogin', 'Review')}
              </Button>
            </span>
          </ButtonGroup>
        </form>
      </div>
    </div>
  );
};

export default CreateLogin;
